import cx from "classnames";

import { Optional } from "common/utils/types";
import LinkButton from "common/components/LinkButton";
import { Destination } from "common/router/types";

import Image from "./Image";

import styles from "../styles/components/CatalogImage.module.less";

type CatalogImageSize = "thumbnail" | "medium" | "large" | "original";

type CatalogImageImage = {
  url: string;
  thumbnail_url: string;
  medium_url: string;
  large_url: string;
};

type CatalogImageProps = {
  image: Optional<CatalogImageImage>;
  alt: string;
  size: CatalogImageSize;
  className?: string;
};

const exhaustiveCheck = (size: never) => {
  console.error("Missing implementation for size: ", size);
};

const getSrc = (props: CatalogImageProps) => {
  const { image, size } = props;

  if (!image) {
    return undefined; // Use the default image fallback
  }

  switch (size) {
    case "original":
      return image.url;
    case "thumbnail":
      return image.thumbnail_url;
    case "large":
      return image.large_url;
    case "medium":
      return image.medium_url;
    default:
      exhaustiveCheck(size);
      return image.medium_url;
  }
};

const getClassNames = ({ className, size }: CatalogImageProps) =>
  cx(className, styles[size], styles.container);

type CatalogImageLinkProps = { dest: Destination; onClick?: () => void } & CatalogImageProps;

const CatalogImageLink = (props: CatalogImageLinkProps) => {
  const { className, dest, onClick, image, size, ...other } = props;
  const classNames = getClassNames(props);
  const src = getSrc(props);

  return (
    <LinkButton onClick={onClick} dest={dest} className={cx(classNames, styles.link)}>
      <Image className={styles.image} src={src} {...other} />
    </LinkButton>
  );
};

type CatalogImageButtonProps = {
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  tabIndex?: number;
} & CatalogImageProps;

const CatalogImageButton = (props: CatalogImageButtonProps) => {
  const { className, onClick, tabIndex, image, size, ...other } = props;
  const classNames = getClassNames(props);
  const src = getSrc(props);

  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={tabIndex}
      className={cx(classNames, styles.link)}
    >
      <Image className={styles.image} src={src} {...other} />
    </button>
  );
};

const CatalogImage = (props: CatalogImageProps) => {
  const { className, image, size, ...other } = props;
  const classNames = getClassNames(props);
  const src = getSrc(props);

  return (
    <div className={classNames}>
      <Image className={styles.image} src={src} {...other} />
    </div>
  );
};

export default Object.assign(CatalogImage, { Link: CatalogImageLink, Button: CatalogImageButton });
